'use client';
// Reviews v2 PREVIEW: first `initial` cards, then "הצג עוד המלצות" reveals `step` more (bughunter 29/09/26 — the old LIMIT 50
// left reviews 51+ unreachable). Order comes from the server (text-first).
import { Children, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import ReviewSlider, { ReviewSlide } from './ReviewSlider';
import { useTagOrder, tagLabel } from './tagContext';
import { trackEvent } from '@/components/Tracker';
import { useDragScroll } from '@/lib/useDragScroll';

// stage 4 (28/10/26, port of experts.reviews Reviews.tsx): filter by what the review is about (theme from the lexicon, or service from
// he_review_topic_tags) + sort. `meta` is aligned with `ids`/children (built in the page from components/ReviewMarks analyzeReview).
// The insights rail (ReviewInsightsPanel) buttons [data-rv-theme]/[data-rv-svc] drive the same filter and scroll here.
export type CardMeta = { t: string[]; n: string[]; s: string[]; r: number | null; d: number; p: boolean };
export type Facets = { themes: { key: string; label: string; color: string; n: number }[]; services: { key: string; label: string; n: number }[] };
type Filter = { kind: 'theme' | 'svc'; key: string } | null;
type Sort = 'rec' | 'new' | 'hi' | 'lo' | 'photo';
const SORTS: [Sort, string][] = [['rec', 'מומלצות'], ['new', 'החדשות'], ['hi', 'דירוג גבוה'], ['lo', 'דירוג נמוך'], ['photo', 'תמונות קודם']];

// label: always 'המלצות' (reviews V5, 30/09/26) — (every pro page reaches all its reviews since 30/09/26)
// M3 (Founder 02/10/26): the cards ride THE review slider (components/ReviewSlider); "הצג עוד" stays — as the slider's last slide.
const DESK_FIRST = 8;
const DESK_STEP = 12;
export default function ReviewsV2More({ children, initial = 50, step = 50, label = 'המלצות', proId, ids, meta, facets }: { children: ReactNode; initial?: number; step?: number; label?: string; proId: number; ids?: number[]; meta?: CardMeta[]; facets?: Facets }) {
  const base = Children.toArray(children);
  // tag-search (14/10/26): reviews whose TAGS match the visitor's search come first (ids = review ids aligned with the children). Neutral order without a search context.
  const { order, reset } = useTagOrder(proId);
  const [filter, setFilter] = useState<Filter>(null);
  const [sort, setSort] = useState<Sort>('rec');
  const okMeta = !!meta && meta.length === base.length;
  const { all, matched } = useMemo(() => {
    let idx = base.map((_, i) => i), matched = 0;
    if (order && ids && ids.length === base.length) {
      const pos = new Map(ids.map((id, i) => [id, i])); const firstIdx = order.reviews.map((id) => pos.get(id)).filter((i): i is number => i != null);
      const f = new Set(firstIdx); idx = [...firstIdx, ...idx.filter((i) => !f.has(i))]; matched = firstIdx.length;
    }
    if (okMeta && filter) idx = idx.filter((i) => filter.kind === 'svc' ? meta![i].s.includes(filter.key) : meta![i].t.includes(filter.key) || meta![i].n.includes(filter.key));
    if (okMeta && sort !== 'rec') {
      const rank = new Map(idx.map((i, k) => [i, k]));   // stable: ties keep the recommended order
      const key = (i: number) => { const m = meta![i]; return sort === 'new' ? -m.d : sort === 'hi' ? -(m.r ?? 0) : sort === 'lo' ? (m.r ?? 6) : m.p ? 0 : 1; };
      idx = [...idx].sort((a, b) => key(a) - key(b) || rank.get(a)! - rank.get(b)!);
    }
    return { all: idx.map((i) => base[i]), matched };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, ids, children, filter, sort, meta]);
  // facet counts from the cards themselves, so a chip's number is exactly what the filter shows
  const counts = useMemo(() => {
    const c = new Map<string, number>();
    if (okMeta) for (const m of meta!) { for (const k of new Set([...m.t, ...m.n])) c.set(`theme:${k}`, (c.get(`theme:${k}`) || 0) + 1); for (const k of m.s) c.set(`svc:${k}`, (c.get(`svc:${k}`) || 0) + 1); }
    return c;
  }, [meta, okMeta]);
  const themeChips = (facets?.themes || []).filter((t) => counts.get(`theme:${t.key}`));
  const svcChips = (facets?.services || []).filter((t) => counts.get(`svc:${t.key}`)).slice(0, 6);
  const hasRated = okMeta && new Set(meta!.filter((m) => m.r != null).map((m) => m.r)).size > 1, hasPhotos = okMeta && meta!.some((m) => m.p);
  const sorts = SORTS.filter(([k]) => (k !== 'hi' && k !== 'lo') || hasRated).filter(([k]) => k !== 'photo' || hasPhotos);
  const showBar = okMeta && base.length >= 3 && (themeChips.length > 0 || svcChips.length > 0 || sorts.length > 1);
  const activeLabel = filter ? ((filter.kind === 'theme' ? facets?.themes : facets?.services) || []).find((x) => x.key === filter.key)?.label ?? '' : null;
  const [shown, setShown] = useState(initial);
  // desktop ≥1024 (direction A, 16/10/26): the same cards as a 2-column grid (CSS .rv-grid-lg), the first DESK_FIRST then ONE "הצג עוד"; phones <768 (21/10/26) the same batch as a vertical list; tablet never reads this
  const [deskShown, setDeskShown] = useState(DESK_FIRST); // batch-based (16/10/26): +DESK_STEP per click, count on the button = what remains
  const left = all.length - Math.max(shown, deskShown);
  const deskLeft = Math.max(0, all.length - deskShown);
  const renderN = Math.max(shown, deskShown);
  // bughunter 02/10 (gate-1.8.1 round): after "הצג עוד" the slider landed on the LAST card (measured live: visible 99–100 of 101).
  // The jump now runs AFTER React committed the new cards (effect on `shown`), on the scroller itself: the first NEW card, snap kept.
  const scroller = useRef<HTMLDivElement | null>(null);
  const landOn = useRef<number | null>(null);
  useEffect(() => {
    const first = landOn.current; const el = scroller.current; landOn.current = null;
    if (first == null || !el) return;
    const card = el.children[first] as HTMLElement | undefined; if (!card) return;
    const snap = el.style.scrollSnapType; el.style.scrollSnapType = 'none';
    const sr = el.getBoundingClientRect(), cr = card.getBoundingClientRect();
    // RTL: the card's right edge meets the scroller's right edge (minus the bleed padding)
    el.scrollLeft += cr.right - (sr.right - parseFloat(getComputedStyle(el).paddingRight || '0'));
    requestAnimationFrame(() => { el.style.scrollSnapType = snap; });
  }, [shown]);
  // tablet 768–1023 (16/10/26): the slider is as tall as its TALLEST card (e.g. a 325px review among 142px ones) → ~150px of empty space under the
  // visible cards. Fit its height to the cards currently in view. Phones (<768) and desktop (≥1024 = grid) are never touched.
  useEffect(() => {
    const el = scroller.current; if (!el) return;
    const mq = window.matchMedia('(min-width:768px) and (max-width:1023px)');
    let raf = 0;
    const fit = () => {
      raf = 0;
      if (!mq.matches) { if (el.style.height) el.style.height = ''; return; }
      el.style.height = '';
      const sr = el.getBoundingClientRect(); let max = 0;
      for (const k of Array.from(el.children) as HTMLElement[]) {
        const r = k.getBoundingClientRect(); const vis = Math.min(r.right, sr.right) - Math.max(r.left, sr.left);
        if (vis > r.width * 0.3) max = Math.max(max, r.height);
      }
      if (max > 0 && max < el.getBoundingClientRect().height) el.style.height = `${Math.ceil(max + (parseFloat(getComputedStyle(el).paddingBottom) || 0))}px`;
    };
    const sched = () => { if (!raf) raf = requestAnimationFrame(fit); };
    fit(); el.addEventListener('scroll', sched, { passive: true }); window.addEventListener('resize', sched); el.addEventListener('click', () => setTimeout(sched, 60));
    const t = setTimeout(sched, 800);
    return () => { el.removeEventListener('scroll', sched); window.removeEventListener('resize', sched); clearTimeout(t); if (raf) cancelAnimationFrame(raf); el.style.height = ''; };
  }, [shown, order, filter, sort]);
  const apply = (f: Filter, s: Sort = sort, from: 'bar' | 'rail' = 'bar') => {
    setFilter(f); setSort(s); setShown(initial); setDeskShown(DESK_FIRST);
    const el = scroller.current; if (el) el.scrollLeft = 0;
    trackEvent('review_filter', { metadata: { proId, kind: f?.kind ?? 'all', key: f?.key ?? null, sort: s, from }, professionalId: proId });
  };
  // the insights rail / inline panel: a theme or service there filters the reviews and brings them into view
  const applyRef = useRef(apply); applyRef.current = apply;
  useEffect(() => {
    if (!okMeta) return;
    const on = (e: MouseEvent) => {
      const b = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-rv-theme],[data-rv-svc]'); if (!b) return;
      const f: Filter = b.dataset.rvTheme ? { kind: 'theme', key: b.dataset.rvTheme } : { kind: 'svc', key: b.dataset.rvSvc! };
      applyRef.current(f, 'rec', 'rail');
      requestAnimationFrame(() => document.getElementById('ct2-reviews')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    };
    document.addEventListener('click', on);
    return () => document.removeEventListener('click', on);
  }, [okMeta]);
  const chipsRef = useRef<HTMLDivElement | null>(null);
  useDragScroll(chipsRef);   // one scrolling row of chips: mouse drag on desktop, swipe on touch
  const isOn = (kind: 'theme' | 'svc', key: string) => filter?.kind === kind && filter.key === key;
  return (
    <div className="rvf-scope" data-focus={filter?.kind === 'theme' ? filter.key : undefined}>
    {showBar && (
      <div className="rvf" role="group" aria-label={`סינון ${label}`}>
        <div className="rvf-chips" ref={chipsRef}>
          <button type="button" className="rvf-c rvf-all" aria-pressed={!filter} onClick={() => apply(null)}>הכל</button>
          {themeChips.map((t) => (
            <button key={t.key} type="button" className="rvf-c" style={{ ['--c' as string]: t.color }} aria-pressed={isOn('theme', t.key)}
              onClick={() => apply(isOn('theme', t.key) ? null : { kind: 'theme', key: t.key })}>
              <i aria-hidden="true" />{t.label} <bdi dir="ltr">{counts.get(`theme:${t.key}`)}</bdi>
            </button>
          ))}
          {svcChips.map((t) => (
            <button key={t.key} type="button" className="rvf-c rvf-svc" aria-pressed={isOn('svc', t.key)}
              onClick={() => apply(isOn('svc', t.key) ? null : { kind: 'svc', key: t.key })}>
              {t.label} <bdi dir="ltr">{counts.get(`svc:${t.key}`)}</bdi>
            </button>
          ))}
        </div>
        {sorts.length > 1 && (
          <label className="rvf-sort">
            <span>מיון</span>
            <select value={sort} onChange={(e) => apply(filter, e.target.value as Sort)}>
              {sorts.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>
        )}
      </div>
    )}
    {filter && (
      <p className="rvf-note" aria-live="polite">
        מציג <bdi dir="ltr">{all.length}</bdi> {label} על {activeLabel}
        <button type="button" onClick={() => apply(null)}>הצג הכל</button>
      </p>
    )}
    {order && matched > 0 && (
      <div data-tag-chip="reviews" className="flex items-center gap-2 mb-2 text-xs" style={{ color: '#1d4ed8' }}>
        <span className="px-2.5 py-1 rounded-full" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>מציג קודם המלצות על: {tagLabel(order)} ({matched})</span>
        <button type="button" onClick={reset} className="underline" style={{ color: '#64748b', minHeight: 32 }}>סדר רגיל</button>
      </div>
    )}
    <style>{`@media (min-width:1024px),(max-width:767px){.rv-slider.rv-grid-lg>.rv-slide:nth-child(n+${deskShown + 1}){display:none}}`}</style>
    <ReviewSlider proId={proId} bleed scrollRef={scroller} className="rv-grid-lg">
      {all.slice(0, renderN).map((c, i) => <ReviewSlide key={i}>{c}</ReviewSlide>)}
      {all.length - renderN > 0 && (
        <div className="flex-shrink-0 flex items-center justify-center rv-inmore" style={{ alignSelf: 'stretch', paddingInline: 8, scrollSnapAlign: 'end' /* patrol 02/10: a mandatory-snap slider must be able to stop here */ }}>
          <button type="button" onClick={() => {
              // patrol 02/10: after loading, land on the FIRST new review — otherwise snap re-lands on this (moved) button and the
              // new reviews are skipped unseen (the jump itself: the effect above, after the commit)
              landOn.current = shown; setShown(s => s + step);
            }}
            className="text-sm font-semibold px-5 py-2.5 rounded-full"
            style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', minHeight: 44, whiteSpace: 'nowrap' }}>
            הצג עוד {label} (<bdi dir="ltr">{left}</bdi>)
          </button>
        </div>
      )}
    </ReviewSlider>
    {deskLeft > 0 && (
      <div className="rv-dmore">
        <button type="button" onClick={() => setDeskShown(n => Math.min(all.length, n + DESK_STEP))}>הצג עוד {label} (<bdi dir="ltr">{deskLeft}</bdi>)</button>
      </div>
    )}
    </div>
  );
}
